import NavNode from './NavNode.js';
import NavGraph from './NavGraph.js';
import SurfaceGrid from './SurfaceGrid.js';
import GridPathfinder from './GridPathfinder.js';
import Debug from '../utils/Debug.js';

/**
 * Builds navigation graph from world cuboids.
 * Creates nodes for walkable surfaces and establishes connections
 * between adjacent/reachable surfaces.
 */
export default class NavMeshBuilder {
  // Maximum height difference for bidirectional walking (step up/down)
  // Character can walk up/down steps of this height without jumping
  static STEP_HEIGHT = 1.0;

  // Maximum height for one-way fall connections
  // Heights > STEP_HEIGHT require falling down (character cannot jump up)
  // Character must find intermediate steps to climb higher surfaces
  static FALL_HEIGHT = 6.0;

  // Character dimensions for clearance checks
  static CHARACTER_WIDTH = 0.9;
  static CHARACTER_HEIGHT = 2.0;
  static CHARACTER_DEPTH = 0.9;

  // Height penalty - climbing up is penalized more than falling down
  static HEIGHT_PENALTY_UP = 0.8;    // Penalty per unit when going UP
  static HEIGHT_PENALTY_DOWN = 0.1;  // Minimal penalty for falling DOWN (gravity is free!)

  // Tolerance for edge adjacency (allows small gaps between cuboids)
  static EDGE_TOLERANCE = 0.5;

  // Maximum gap between surfaces that can still be traversed (walking across gaps)
  static MAX_GAP = 1.0;

  // Grid cell size for fine-grained pathfinding on surfaces
  static GRID_CELL_SIZE = 0.5;

  /**
   * Create a navigation mesh builder
   * @param {World} world - The game world
   * @param {CharacterCollisionSystem} collisionSystem - For clearance checks
   */
  constructor(world, collisionSystem) {
    this.world = world;
    this.collisionSystem = collisionSystem;
    this.graph = new NavGraph();
    this.dirty = true;
  }

  /**
   * Build or rebuild the navigation graph from current world state
   * @returns {NavGraph}
   */
  build() {
    this.graph.clear();

    // Phase 1: Create nodes for all cuboid top surfaces
    for (const [key, cuboid] of this.world.cuboids) {
      // Skip fragments - they're visual subdivisions only
      if (cuboid.isFragment) continue;

      // Skip non-collider cuboids (water, gas, etc.) - can't walk on them
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      // Check if this surface is walkable (has enough head clearance)
      if (!this._isWalkableSurface(cuboid)) {
        continue;
      }

      const node = this._createNodeFromCuboid(key, cuboid);
      this.graph.addNode(node);
    }

    // Phase 2: Build adjacency connections
    let totalConnections = 0;
    for (const node of this.graph.getAllNodes()) {
      const beforeCount = node.neighbors.length;
      this._findAndConnectNeighbors(node);
      totalConnections += node.neighbors.length - beforeCount;
    }

    // Phase 3: Build surface grids for fine-grained pathfinding
    this._buildSurfaceGrids();

    this.dirty = false;
    Debug.log('NavMesh', `NavMesh built: ${this.graph.size} nodes, ${totalConnections} total connections`);

    // Debug: log nodes and identify isolated ones
    let count = 0;
    let isolatedCount = 0;
    for (const node of this.graph.getAllNodes()) {
      if (node.neighbors.length === 0) {
        isolatedCount++;
        if (isolatedCount <= 3) {
          Debug.warn('NavMesh', `  ISOLATED Node ${node.cuboidKey}: y=${node.y.toFixed(1)}, bounds(${node.minX.toFixed(1)}-${node.maxX.toFixed(1)}, ${node.minZ.toFixed(1)}-${node.maxZ.toFixed(1)})`);
        }
      } else if (count < 5) {
        Debug.log('NavMesh', `  Node ${node.cuboidKey}: y=${node.y.toFixed(1)}, neighbors: ${node.neighbors.length}`);
        count++;
      }
    }
    if (isolatedCount > 0) {
      Debug.warn('NavMesh', `  Total isolated nodes: ${isolatedCount}`);
    }

    return this.graph;
  }

  /**
   * Rebuild the graph if marked dirty
   */
  rebuildIfDirty() {
    if (this.dirty) {
      this.build();
    }
  }

  /**
   * Mark the graph as needing rebuild
   */
  invalidate() {
    this.dirty = true;
  }

  /**
   * Invalidate a specific region (for incremental updates)
   * Currently just marks entire graph dirty - could be optimized
   * @param {Object} cuboid - The changed cuboid
   */
  invalidateRegion(cuboid) {
    // For simplicity, invalidate entire graph
    // A more sophisticated implementation could do incremental updates
    this.dirty = true;
  }

  /**
   * Get the current navigation graph
   * @returns {NavGraph}
   */
  getGraph() {
    this.rebuildIfDirty();
    return this.graph;
  }

  /**
   * Check if a cuboid's top surface is walkable (has head clearance)
   * @private
   */
  _isWalkableSurface(cuboid) {
    const surfaceY = cuboid.y + cuboid.height;
    const centerX = cuboid.x + cuboid.width / 2;
    const centerZ = cuboid.z + cuboid.depth / 2;

    // Query for obstacles above this surface
    const queryBox = {
      minX: cuboid.x + 0.1,
      minY: surfaceY + 0.01,
      minZ: cuboid.z + 0.1,
      maxX: cuboid.x + cuboid.width - 0.1,
      maxY: surfaceY + NavMeshBuilder.CHARACTER_HEIGHT,
      maxZ: cuboid.z + cuboid.depth - 0.1
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      // Skip self
      if (obstacle === cuboid) continue;

      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;

      // Skip if obstacle is entirely below our surface
      if (obsTop <= surfaceY + 0.01) continue;

      // Check if obstacle blocks head space on a significant portion of the surface
      // We need at least some area clear to walk
      const overlapX = Math.max(0,
        Math.min(obstacle.x + obstacle.width, cuboid.x + cuboid.width) -
        Math.max(obstacle.x, cuboid.x)
      );
      const overlapZ = Math.max(0,
        Math.min(obstacle.z + obstacle.depth, cuboid.z + cuboid.depth) -
        Math.max(obstacle.z, cuboid.z)
      );
      const overlapArea = overlapX * overlapZ;
      const surfaceArea = cuboid.width * cuboid.depth;

      // If obstacle covers more than 80% of the surface, check if there's still
      // enough exposed margin for the character to stand on (staircase steps have
      // high coverage but the exposed ledge is where you walk)
      if (overlapArea > surfaceArea * 0.8 && obsBottom < surfaceY + NavMeshBuilder.CHARACTER_HEIGHT) {
        const exposedLeft = Math.max(0, obstacle.x - cuboid.x);
        const exposedRight = Math.max(0, (cuboid.x + cuboid.width) - (obstacle.x + obstacle.width));
        const exposedFront = Math.max(0, obstacle.z - cuboid.z);
        const exposedBack = Math.max(0, (cuboid.z + cuboid.depth) - (obstacle.z + obstacle.depth));
        const maxExposedMargin = Math.max(exposedLeft, exposedRight, exposedFront, exposedBack);

        // If no edge has enough room for the character, surface is blocked
        if (maxExposedMargin < NavMeshBuilder.CHARACTER_WIDTH) {
          return false;
        }
        // Otherwise, there's a walkable strip despite high coverage (staircase step)
      }
    }

    return true;
  }

  /**
   * Create a NavNode from a cuboid
   * @private
   */
  _createNodeFromCuboid(key, cuboid) {
    // Node represents the center of the top surface
    const x = cuboid.x + cuboid.width / 2;
    const y = cuboid.y + cuboid.height;  // Top surface
    const z = cuboid.z + cuboid.depth / 2;

    return new NavNode(key, x, y, z, cuboid.width, cuboid.depth);
  }

  /**
   * Find and connect neighbors for a node
   * @private
   */
  _findAndConnectNeighbors(node) {
    const potentialNeighbors = this.graph.getPotentialNeighbors(node);

    for (const other of potentialNeighbors) {
      // Skip if already connected
      if (node.neighbors.some(n => n.node === other)) continue;

      // Check traversal type
      const traverseType = this._getTraverseType(node, other);

      if (traverseType === 'walk') {
        // Bidirectional connection (walking between surfaces at same or ≤1 unit height diff)
        const costForward = this._calculateCost(node, other);
        const costBackward = this._calculateCost(other, node);

        // Slight discount for going down (gravity helps)
        const heightDiff = other.y - node.y;

        // Forward direction (node -> other)
        const forwardCost = heightDiff > 0 ?
          costForward :        // Going up - normal cost
          costForward * 0.9;   // Going down - small discount

        // Backward direction (other -> node)
        const backwardCost = heightDiff < 0 ?
          costBackward :       // Going up - normal cost
          costBackward * 0.9;  // Going down - small discount

        node.addNeighbor(other, forwardCost);
        other.addNeighbor(node, backwardCost);
      } else if (traverseType === 'fall') {
        // One-way fall connection (from higher to lower)
        // Falls are cheap - gravity does the work!
        const cost = this._calculateCost(node, other) * 0.7; // 30% discount for falls
        if (node.y > other.y) {
          node.addNeighbor(other, cost);
        } else {
          other.addNeighbor(node, cost);
        }
      }
    }
  }

  /**
   * Determine what type of traversal is possible between two nodes
   * @private
   * @returns {'walk'|'jump'|'fall'|null} Type of traversal or null if not possible
   */
  _getTraverseType(from, to) {
    const heightDiff = Math.abs(to.y - from.y);

    // Check if surfaces overlap on XZ (one is "on top of" the other)
    const overlapping = from.overlapsXZ(to);

    // Check if nodes are adjacent or near enough to traverse
    if (!overlapping && !this._areNodesNearAdjacent(from, to)) {
      return null;
    }

    // For OVERLAPPING surfaces (stacked vertically):
    if (overlapping) {
      if (heightDiff <= NavMeshBuilder.STEP_HEIGHT) {
        return 'walk'; // Bidirectional - character can walk up/down 1-unit steps
      }
      // Character CANNOT jump - any height > STEP_HEIGHT is fall-only (one-way down)
      if (heightDiff <= NavMeshBuilder.FALL_HEIGHT) {
        return 'fall'; // One-way fall - character must find intermediate steps to go up
      }
      return null; // Too high to traverse
    }

    // For ADJACENT (non-overlapping) surfaces:
    // Check if there's a real gap between the surfaces (vs just touching edges).
    // A character can't step up across a gap - they need an intermediate bridging surface.
    // Example: narrow platform (Y=1) with 1-unit gap to top platform (Y=2) should route
    // through a step cuboid that bridges the gap, not directly across.
    const isTouchingEdge = from.touchesXZ(to, NavMeshBuilder.EDGE_TOLERANCE);

    if (heightDiff <= NavMeshBuilder.STEP_HEIGHT) {
      if (!isTouchingEdge && heightDiff > 0.1) {
        // Gap + height difference: can't step up/down across a gap without a bridge.
        // No connection - force A* to find intermediate surfaces.
        return null;
      }
      if (this._hasClearance(from, to)) {
        return 'walk';
      }
      return null;
    }

    // For larger height differences on adjacent surfaces: one-way fall only
    if (heightDiff <= NavMeshBuilder.FALL_HEIGHT) {
      return 'fall';
    }

    return null;
  }


  /**
   * Check if two nodes are near-adjacent (can walk between them)
   * This is more permissive than strict edge touching
   * @private
   */
  _areNodesNearAdjacent(from, to) {
    // If they overlap, they're connected
    if (from.overlapsXZ(to)) {
      return true;
    }

    // If they touch edges (with tolerance), they're connected
    if (from.touchesXZ(to, NavMeshBuilder.EDGE_TOLERANCE)) {
      return true;
    }

    // Check for small gap traversal
    // Calculate the gap between nodes on each axis
    const gapX = Math.max(0,
      Math.max(from.minX, to.minX) - Math.min(from.maxX, to.maxX)
    );
    const gapZ = Math.max(0,
      Math.max(from.minZ, to.minZ) - Math.min(from.maxZ, to.maxZ)
    );

    // If there's a gap on both axes (diagonal gap), check if it's within range
    if (gapX > 0 && gapZ > 0) {
      // Diagonal distance must be walkable
      const diagonalGap = Math.sqrt(gapX * gapX + gapZ * gapZ);
      return diagonalGap <= NavMeshBuilder.MAX_GAP;
    }

    // If gap is only on one axis, check if it's traversable
    // and that there's overlap on the other axis
    if (gapX > 0 && gapX <= NavMeshBuilder.MAX_GAP) {
      // Check Z overlap
      return from.minZ < to.maxZ && from.maxZ > to.minZ;
    }
    if (gapZ > 0 && gapZ <= NavMeshBuilder.MAX_GAP) {
      // Check X overlap
      return from.minX < to.maxX && from.maxX > to.minX;
    }

    return false;
  }

  /**
   * Check if there's clearance for a character between two nodes
   * @private
   */
  _hasClearance(from, to) {
    // Find the actual connection point where the two surfaces meet.
    // For adjacent surfaces, this is at the shared edge (not the midpoint between centers,
    // which can be far from the edge for differently-sized surfaces).
    const overlapMinX = Math.max(from.minX, to.minX);
    const overlapMaxX = Math.min(from.maxX, to.maxX);
    const overlapMinZ = Math.max(from.minZ, to.minZ);
    const overlapMaxZ = Math.min(from.maxZ, to.maxZ);

    let midX, midZ;
    if (overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ) {
      // Surfaces overlap on XZ - use center of overlap region
      midX = (overlapMinX + overlapMaxX) / 2;
      midZ = (overlapMinZ + overlapMaxZ) / 2;
    } else {
      // Surfaces are adjacent (touching edges) - find the shared edge point
      // X axis: check if they touch on X
      const touchesXRight = Math.abs(from.maxX - to.minX) <= NavMeshBuilder.EDGE_TOLERANCE;
      const touchesXLeft = Math.abs(from.minX - to.maxX) <= NavMeshBuilder.EDGE_TOLERANCE;
      // Z axis: check if they touch on Z
      const touchesZFront = Math.abs(from.maxZ - to.minZ) <= NavMeshBuilder.EDGE_TOLERANCE;
      const touchesZBack = Math.abs(from.minZ - to.maxZ) <= NavMeshBuilder.EDGE_TOLERANCE;

      if (touchesXRight || touchesXLeft) {
        // Shared X edge - use the edge X, center of Z overlap
        midX = touchesXRight ? from.maxX : from.minX;
        const zOverlapMin = Math.max(from.minZ, to.minZ);
        const zOverlapMax = Math.min(from.maxZ, to.maxZ);
        midZ = (zOverlapMin + zOverlapMax) / 2;
      } else if (touchesZFront || touchesZBack) {
        // Shared Z edge - use the edge Z, center of X overlap
        midZ = touchesZFront ? from.maxZ : from.minZ;
        const xOverlapMin = Math.max(from.minX, to.minX);
        const xOverlapMax = Math.min(from.maxX, to.maxX);
        midX = (xOverlapMin + xOverlapMax) / 2;
      } else {
        // Gap traversal - use midpoint between closest edges
        midX = (Math.min(from.maxX, to.maxX) + Math.max(from.minX, to.minX)) / 2;
        midZ = (Math.min(from.maxZ, to.maxZ) + Math.max(from.minZ, to.minZ)) / 2;
      }
    }

    const midY = Math.max(from.y, to.y);  // Use higher surface

    // Query for obstacles at the connection point
    const halfWidth = NavMeshBuilder.CHARACTER_WIDTH / 2;
    const halfDepth = NavMeshBuilder.CHARACTER_DEPTH / 2;

    const queryBox = {
      minX: midX - halfWidth,
      minY: midY + 0.01,  // Just above surface
      minZ: midZ - halfDepth,
      maxX: midX + halfWidth,
      maxY: midY + NavMeshBuilder.CHARACTER_HEIGHT,
      maxZ: midZ + halfDepth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    // Filter out obstacles that don't actually block the path
    for (const obstacle of obstacles) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsTop = obstacle.y + obstacle.height;
      const obsBottom = obstacle.y;

      // Skip if this is a floor we're standing on (from or to surface)
      // i.e., the obstacle's top surface IS the walkable surface
      if (Math.abs(obsTop - from.y) < 0.1) continue;
      if (Math.abs(obsTop - to.y) < 0.1) continue;

      // Skip if obstacle is entirely below our walking surface
      if (obsTop <= midY + 0.01) continue;

      // Check if obstacle actually blocks the path (overhead obstacle)
      if (obsBottom < midY + NavMeshBuilder.CHARACTER_HEIGHT) {
        // Check XZ overlap
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
            queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
          return false;  // Blocked
        }
      }
    }

    return true;
  }

  /**
   * Calculate traversal cost between two nodes
   * Uses closest point distance for overlapping surfaces to avoid underestimating
   * @private
   */
  _calculateCost(from, to) {
    const dy = to.y - from.y;

    // Check if surfaces overlap on XZ
    const overlapsXZ = from.overlapsXZ(to);

    let horizontalDist;

    if (overlapsXZ) {
      // For overlapping surfaces, cost is based on the overlap region
      // The actual walking distance depends on where the transition point will be
      // Use minimum distance to edge of overlap as base cost
      const overlapMinX = Math.max(from.minX, to.minX);
      const overlapMaxX = Math.min(from.maxX, to.maxX);
      const overlapMinZ = Math.max(from.minZ, to.minZ);
      const overlapMaxZ = Math.min(from.maxZ, to.maxZ);

      // Distance from center of 'from' to nearest point in overlap
      const nearestX = Math.max(overlapMinX, Math.min(overlapMaxX, from.x));
      const nearestZ = Math.max(overlapMinZ, Math.min(overlapMaxZ, from.z));

      const dx = nearestX - from.x;
      const dz = nearestZ - from.z;
      horizontalDist = Math.sqrt(dx * dx + dz * dz);

      // Add small base cost for any vertical transition
      horizontalDist += 0.5;
    } else {
      // For adjacent surfaces, use edge-to-edge distance
      // Find closest points on the two surfaces
      const closestX1 = Math.max(from.minX, Math.min(from.maxX, to.x));
      const closestZ1 = Math.max(from.minZ, Math.min(from.maxZ, to.z));
      const closestX2 = Math.max(to.minX, Math.min(to.maxX, from.x));
      const closestZ2 = Math.max(to.minZ, Math.min(to.maxZ, from.z));

      const dx = closestX2 - closestX1;
      const dz = closestZ2 - closestZ1;
      horizontalDist = Math.sqrt(dx * dx + dz * dz);
    }

    // 3D cost
    let cost = Math.sqrt(horizontalDist * horizontalDist + dy * dy);

    // Add asymmetric penalty: climbing up is harder than falling down
    if (dy > 0.1) {
      // Going UP - penalize more
      cost += dy * NavMeshBuilder.HEIGHT_PENALTY_UP;
    } else if (dy < -0.1) {
      // Going DOWN - minimal penalty (gravity is free!)
      cost += Math.abs(dy) * NavMeshBuilder.HEIGHT_PENALTY_DOWN;
    }

    return cost;
  }

  /**
   * Build surface grids for all nodes
   * This enables fine-grained pathfinding within each surface
   * @private
   */
  _buildSurfaceGrids() {
    let gridCount = 0;
    let totalCells = 0;

    for (const node of this.graph.getAllNodes()) {
      // Create surface grid for this node
      const grid = new SurfaceGrid(
        node,
        this.collisionSystem,
        NavMeshBuilder.GRID_CELL_SIZE
      );

      // Create pathfinder for this grid
      const pathfinder = new GridPathfinder(grid);

      // Attach to node
      node.setGrid(grid, pathfinder);

      gridCount++;
      totalCells += grid.width * grid.height;
    }

    Debug.log('NavMesh', `  Surface grids built: ${gridCount} grids, ${totalCells} total cells`);
  }
}
